import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { VendorService } from '../../../services/vendor.service';
import { GlobalSearchService } from 'app/services/globalsearchservice.service';

/** Filter chip selections for the Over Due Vendors card. */
export type OverdueDateMode = 'today' | '30d' | 'custom';

@Component({
	selector: 'app-vendordash-overdue-as-of',
	templateUrl: './vendordash-overdue-as-of.component.html',
	styleUrls: ['./vendordash-overdue-as-of.component.scss'],
	host: { class: 'vendordash-asof' },
})

export class VendordashOverdueAsOfComponent implements OnInit {
	/** Active filter chip: today | 30d | custom */
	overdueDateMode: OverdueDateMode = '30d';

	/**
	 * Custom date picked by the user when overdueDateMode === 'custom'.
	 * Bound to the Material datepicker inside the flyout menu.
	 */
	customAsOfDate: Date | null = null;

	/** Resolved aged data passed down to the child overdue component. */
	agedResults: any = null;

	/** Label shown on the card when a custom date is active (e.g. "5/6/2026"). */
	customDateLabel = '';


	config: any = false;
	allow_as_of: any = true;
	@Output() viewData = new EventEmitter<any>();
	@ViewChild('customMenuTrigger') customMenuTrigger: MatMenuTrigger;

	constructor(private vendorService: VendorService, private globalSearchService: GlobalSearchService) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r
			if(r.env.package === 'tires'){
				//no as of for CLV
				this.allow_as_of = false;
			}
		});


	}

	ngOnInit(): void {
		this.refreshAged();
	}

	/** Called when a filter chip is clicked (Today / 30d). */
	selectMode(mode: OverdueDateMode): void {
		this.overdueDateMode = mode;
		// Custom mode is handled separately via applyCustomDate()
		if (mode !== 'custom') {
			this.refreshAged();
		}
	}

	/**
	 * Called from the Custom flyout Apply button.
	 * Closes the menu (via the trigger) and reloads data for the chosen date.
	 */
	applyCustomDate(): void {
		if (this.customAsOfDate) {
			this.overdueDateMode = 'custom';
			this.customDateLabel = this.formatDateLabel(this.customAsOfDate);
			this.refreshAged();
			this.customMenuTrigger?.closeMenu();
		}
	}

	/** Called from the Custom flyout Clear button to reset back to 30d default. */
	clearCustomDate(): void {
		this.customAsOfDate = null;
		this.customDateLabel = '';
		this.overdueDateMode = '30d';
		this.refreshAged();
	}

	// -------------------------------------------------------------------------
	// Private helpers
	// -------------------------------------------------------------------------

	/** Public so the child card can request a re-fetch after a payment posts. */
	refreshAged(): void {
		this.agedResults = null;

		if (this.overdueDateMode === 'today' || !this.allow_as_of) {
			// Current / live data – no date filter
			this.vendorService.getDashBoard().subscribe((results: any) => {
				this.agedResults = results && results.aged != null ? results.aged : [];
			});
			return;
		}

		if (this.overdueDateMode === 'custom') {
			// Use the explicitly chosen date
			const ymd = this.asOfDateToYmd(this.customAsOfDate);
			if (this.isValidYmd(ymd)) {
				this.vendorService.getDashBoard({ as_of_date: ymd }).subscribe((results: any) => {
					this.agedResults = results && results.aged != null ? results.aged : [];
				});
			} else {
				// Fallback to current if no valid date yet
				this.vendorService.getDashBoard().subscribe((results: any) => {
					this.agedResults = results && results.aged != null ? results.aged : [];
				});
			}
			return;
		}

		// 30d – calculate the as-of date relative to today
		const offsetDays = 30;
		const asOf = new Date();
		asOf.setDate(asOf.getDate() - offsetDays);
		const ymd = this.asOfDateToYmd(asOf);
		this.vendorService.getDashBoard({ as_of_date: ymd }).subscribe((results: any) => {
			this.agedResults = results && results.aged != null ? results.aged : [];
		});
	}

	private asOfDateToYmd(d: Date | null): string {
		if (!d) {
			return '';
		}
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private isValidYmd(s: string): boolean {
		if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) {
			return false;
		}
		const t = Date.parse(s + 'T12:00:00');
		return !Number.isNaN(t);
	}

	private formatDateLabel(d: Date): string {
		if (!d) {
			return '';
		}
		return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
	}
}
